import type {
  ServicesChannelsWhatsappComponentParameter,
  ServicesChannelsWhatsappComponentParameters,
  ServicesChannelsWhatsappMessageTemplate,
  ServicesChannelsWhatsappMessageTemplateComponent,
  ServicesChannelsWhatsappTemplateParameters,
} from '@customerio/hydra-ui-api';
import { isEmptyString } from '@/utils/string';

// A WhatsApp component parameter is a flat DS schema with optional `text`/`url`
// (not a discriminated union), so the guards narrow on value presence.
export type ComponentParameter = ServicesChannelsWhatsappComponentParameter;

export function isTextParameter(p: ComponentParameter): p is ComponentParameter & { text: string } {
  return typeof p.text === 'string';
}

export function isUrlParameter(p: ComponentParameter): p is ComponentParameter & { url: string } {
  return typeof p.url === 'string';
}

type ComponentParameters = ServicesChannelsWhatsappComponentParameters;

// The delivery `detail.body` envelope is not the DS template-management schema:
// its keys are all optional (a body may be plain text, or the template pieces),
// so it stays local. Its members derive from the generated schemas.
type WhatsappTemplateBody = {
  body?: string;
  whatsapp_message_template?: ServicesChannelsWhatsappMessageTemplate;
  template_parameters?: ServicesChannelsWhatsappTemplateParameters;
};

function isWhatsappTemplateBody(obj: unknown): obj is WhatsappTemplateBody {
  if (typeof obj !== 'object' || obj == null) {
    return false;
  }
  if ('body' in obj && typeof obj.body === 'string') {
    return true;
  }
  return 'whatsapp_message_template' in obj && 'template_parameters' in obj;
}

export function parseWhatsappTemplateBody(body?: string): WhatsappTemplateBody {
  if (isEmptyString(body)) {
    return { body: '' };
  }

  let data: unknown = null;
  try {
    data = JSON.parse(body ?? '');
  } catch (error) {
    throw new Error('Invalid WhatsApp template body', { cause: error });
  }

  if (isWhatsappTemplateBody(data)) {
    return data;
  }
  throw new Error('Invalid WhatsApp template body');
}

/** A flattened piece of a rendered WhatsApp message, ready to lay out in the bubble. */
export type WhatsappPreviewComponent = {
  type: 'body' | 'header' | 'footer' | 'button';
  previewType?: string;
  text?: string;
  url?: string;
};

const TEXT_COMPONENT_TYPES = ['HEADER', 'BODY', 'FOOTER'];

/** Substitute the named text variables and pull out a media url + its preview type. */
function applyParameters(
  text: string,
  parameters: ComponentParameter[] | undefined,
): { text: string; url?: string; previewType?: string } {
  let resolved = text;
  let url: string | undefined;
  let previewType: string | undefined;

  for (const param of parameters ?? []) {
    if (isTextParameter(param) && param.text.length > 0) {
      // Literal replace — never build a RegExp from the API-supplied parameter name
      // (regex injection / ReDoS); the replacer fn keeps `$` in the text literal too.
      resolved = resolved.replaceAll(`{{${param.parameter_name}}}`, () => param.text);
    } else if (isUrlParameter(param) && param.url.length > 0) {
      url = param.url;
      previewType = param.type.toLowerCase();
    }
  }

  return { text: resolved, url, previewType };
}

function mapComponent(
  component: ServicesChannelsWhatsappMessageTemplateComponent,
  params: ComponentParameters | undefined,
): WhatsappPreviewComponent[] {
  if (TEXT_COMPONENT_TYPES.includes(component.type)) {
    const { text, url, previewType } = applyParameters(component.text, params?.parameters);
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return [{ type: component.type.toLowerCase() as 'header' | 'body' | 'footer', text, url, previewType }];
  }

  if (component.type === 'BUTTONS') {
    return (component.buttons ?? []).map((button) => ({
      type: 'button',
      text: button.type === 'COPY_CODE' ? 'Copy offer code' : button.text,
    }));
  }

  return [];
}

/**
 * Flatten a WhatsApp template delivery body into the pieces the preview bubble
 * renders. Bodies that can't be parsed as a template degrade to a placeholder
 * bubble rather than tearing down the preview.
 */
export function toWhatsappPreviewComponents(body: string): WhatsappPreviewComponent[] {
  if (isEmptyString(body)) {
    return [{ type: 'body', text: '…' }];
  }

  let templateBody: WhatsappTemplateBody;
  try {
    templateBody = parseWhatsappTemplateBody(body);
  } catch {
    return [{ type: 'body', text: '…' }];
  }

  const components = templateBody.whatsapp_message_template?.components ?? [];
  if (components.length === 0) {
    return [{ type: 'body', text: isEmptyString(templateBody.body) ? '…' : templateBody.body! }];
  }

  return components.flatMap((component) => {
    const params = templateBody.template_parameters?.components?.find((p) => p.type === component.type);
    return mapComponent(component, params);
  });
}
